// @ts-strict-ignore
/* eslint-disable @typescript-eslint/dot-notation */
import { BooleanRule, IndexedRule } from 'flow/store/engine/types';
import type { Rules, Threshold } from 'flow/store/engine/types';
import { buildGroupRule, buildThresholdRule } from './rule';
import { groupType as groupTypeConsts, ruleInsertionPosition, ruleType } from './types';
import type {
  RuleBuilderRule,
  RulePath,
  RulePathSegment,
  RuleInsertionPosition,
  GroupRule,
  RuleTree,
} from './types';
import {
  getParentToChildRuleSegment,
  getPathToParent,
  isAmbiguousGroup,
  isBooleanRule, isGroupRule, isIndexedRule, isThresholdRule,
  isTreeRoot,
  shouldInsertIntoRelativeRule,
} from './helpers';
import { generatePublicId } from 'flow/service/publicIds';

// returns a shallow copy of the array with the new rule path segment appended
export function appendRulePath(rulePath: RulePath, newPathEntry: RulePathSegment): RulePath {
  const shallowCopyPath = rulePath.slice();
  shallowCopyPath.push(newPathEntry);
  return shallowCopyPath;
}

function buildRuleSubTree(rule: Rules, rulePath: RulePath): RuleBuilderRule {
  // base case: threshold
  if (isIndexedRule(rule)) {
    return buildThresholdRule({ rulePath, thresholdId: rule['id'] });
  }

  // recursive case: group
  if (isBooleanRule(rule)) {
    const groupKey = Object.hasOwn(rule, groupTypeConsts.and)
      ? groupTypeConsts.and
      : groupTypeConsts.or;
    const children = rule[groupKey];

    const builtChildren = children.map((childRule, index) => {
      const childRulePath = appendRulePath(rulePath, { groupType: groupKey, index });
      return buildRuleSubTree(childRule, childRulePath);
    });

    return buildGroupRule({
      children: builtChildren,
      groupType: groupKey,
      rulePath,
      isAmbiguous: Boolean(rule.ambiguous),
      id: rule.publicId,
    });
  }

  // should not reach this
  return null;
}

// returns the root to the tree
export function buildRuleTree(rules: Rules): RuleTree | null {
  const ruleTree = buildRuleSubTree(rules, []);
  if (isThresholdRule(ruleTree)) {
    throw new Error('Malformed rule set - rules should always be encased within a group.');
  }
  return ruleTree;
}

function buildRuleSubState(rule: RuleBuilderRule): IndexedRule | BooleanRule {
  if (isThresholdRule(rule)) {
    return { id: rule.id } satisfies IndexedRule;
  }
  // group case
  const children = rule.data.children.map(child => buildRuleSubState(child));

  if (rule.data.isAmbiguous) {
    return {
      [rule.data.groupType]: children,
      ambiguous: true,
      publicId: rule.id,
    } satisfies BooleanRule;
  }

  return {
    [rule.data.groupType]: children,
    publicId: rule.id,
  } satisfies BooleanRule;
}

export function buildRuleState(rules: RuleBuilderRule): IndexedRule | BooleanRule | {} {
  if (!rules) {
    return {};
  }
  return buildRuleSubState(rules);
}

/**
 * Accepts a tree root rule and function that performs a test on each rule using DFS.
 * Returns true if any rule satisfies the test condition.
*/
export function testRuleTree(
  treeRoot: RuleTree,
  test: (rule: RuleBuilderRule) => boolean,
) {
  function traverse(rule: RuleBuilderRule) {
    if (test(rule)) {
      return true;
    }
    if (isThresholdRule(rule)) {
      return false;
    }
    for (const child of rule.data.children) {
      const result = traverse(child);
      if (result) {
        return true;
      }
    }
    return false;
  }

  return traverse(treeRoot);
}

/** Tests rule tree for whether or not it has more than 1 threshold. */
export function hasMultipleThresholds(treeRoot: RuleTree) {
  let thresholdCount = 0;

  function test(rule: RuleBuilderRule) {
    if (isThresholdRule(rule)) {
      thresholdCount += 1;
    }
    if (thresholdCount > 1) {
      return true;
    }
    return false;
  }

  return testRuleTree(treeRoot, test);
}

function findRuleById(
  treeRoot: RuleTree,
  ruleId: RuleBuilderRule['id'],
) {
  function traverse(rule: RuleBuilderRule) {
    if (rule.id === ruleId) {
      return rule;
    }
    if (isThresholdRule(rule)) {
      return null;
    }
    for (const child of rule.data.children) {
      const result = traverse(child);
      if (result) {
        return result;
      }
    }
    return null;
  }

  return traverse(treeRoot);
}

function findRuleByRulePath(
  treeRoot: RuleTree,
  rulePath: RulePath,
) {
  function traverse(rule: RuleBuilderRule, remainingPath: RulePath) {
    if (remainingPath.length === 0) {
      return rule;
    }
    if (rule.type === ruleType.group) {
      const ruleSegment = remainingPath.shift();
      return traverse(
        rule.data.children[ruleSegment.index],
        remainingPath,
      );
    }
    // should not reach this point if rulePath is well formed
    return null;
  }

  return traverse(treeRoot, structuredClone(rulePath));
}

export function findRule(
  treeRoot: RuleTree,
  opts: { ruleId: RuleBuilderRule['id'] },
): RuleBuilderRule;

export function findRule(
  treeRoot: RuleTree,
  opts: { rulePath: RulePath },
): RuleBuilderRule;

export function findRule(
  treeRoot: RuleTree,
  opts: {
    ruleId?: RuleBuilderRule['id'];
    rulePath?: RulePath;
  },
): RuleBuilderRule {
  if (opts.ruleId) {
    return findRuleById(treeRoot, opts.ruleId);
  }

  if (opts.rulePath) {
    return findRuleByRulePath(treeRoot, opts.rulePath);
  }

  return null;
}

/** Gets a rule's parent by using rule's defined rulepath */
export function getParentRule(treeRoot: RuleTree, rule: RuleBuilderRule) {
  if (isTreeRoot(rule)) {
    return null;
  }
  const parentRule = findRule(treeRoot, { rulePath: getPathToParent(rule.data.path) });
  // narrow type
  return isGroupRule(parentRule) ? parentRule : null;
}

/**
 * Returns a list of the rules preceding the supplied rule, not including the supplied rule.
 * The rules returned are a copy of the original.
 */
export function getParentRules(treeRoot: RuleTree, rule: RuleBuilderRule) {
  const parentRules: GroupRule[] = [];

  if (isThresholdRule(treeRoot)) {
    return parentRules;
  }

  let lastRule: GroupRule = treeRoot;
  rule.data.path.forEach(segment => {
    parentRules.push(structuredClone(lastRule));

    if (isGroupRule(lastRule)) {
      const nextRule = lastRule.data.children[segment.index];
      if (isGroupRule(nextRule)) {
        lastRule = nextRule;
      }
    }
  });

  return parentRules;
}

/**
 * Returns a copy of the rule child at the specified index.
 * Out of bounds indices, both positive and negatives, return `null`
 */
export function getChildRule(treeRoot: GroupRule, childIndex: number) {
  if (childIndex < 0) {
    return null;
  }

  if (childIndex >= treeRoot.data.children.length) {
    return null;
  }

  return structuredClone(treeRoot.data.children.at(childIndex));
}


/**
* MUTATES all child rules to update their rule paths.
* We choose mutation as it is easier to run on only the subtrees that have changed,
* rather than running it on the entire rule tree.
*/
function rebuildRulePaths(rules: RuleBuilderRule) {
  function traverse(
    rule: RuleBuilderRule, rulePathAccumulator: RulePath,
  ) {
    rule.data.path = rulePathAccumulator;

    if (isThresholdRule(rule)) {
      return;
    }

    rule.data.children.forEach((child, index) => {
      const updatedRulePath = appendRulePath(
        rulePathAccumulator,
        { groupType: rule.data.groupType, index },
      );
      traverse(child, updatedRulePath);
    });
  }

  traverse(rules, rules.data.path);
}

function deleteRuleFromChildren(
  rules: RuleBuilderRule[],
  indexToDelete: number,
  replacement?: RuleBuilderRule[],
) {
  const deletedState: RuleBuilderRule[] = [];

  rules.forEach((rule, index) => {
    if (index !== indexToDelete) {
      deletedState.push(rule);
    } else if (replacement) {
      deletedState.push(...replacement);
    }
  });

  return deletedState;
}

/**
* Deletes the specified rule from the rule tree.
* Returns a copy of the tree.
*/
export function deleteRuleFromRuleTree(args: {
  treeRoot: RuleTree;
  rule: RuleBuilderRule;
}): RuleTree {
  const { treeRoot, rule } = args;

  // delete the whole tree if we are removing the only leaf rule
  if (isThresholdRule(rule) && !hasMultipleThresholds(treeRoot)) {
    return null;
  }

  /*
  * To delete a rule, we create a pointer to that rule's parent.
  * If the rule to be deleted is a group, this allows us to move the deleted group's children
  * up a tree level to become the children of the deleted group's parent.
  * If the rule to be deleted is a threshold, this allows us to maintain the ordering of
  * the parent's children.
  */

  const ruleTree = structuredClone(treeRoot);
  const rulePath = rule.data.path;

  // deleted rule's parent
  const parentRule = getParentRule(ruleTree, rule);

  // last leg of rule path that paths from parent -> rule to be inserted
  const parentToRuleSegment = getParentToChildRuleSegment(rulePath);

  // narrow type
  if (isGroupRule(parentRule)) {
    if (isThresholdRule(rule)) {
      parentRule.data.children = deleteRuleFromChildren(
        parentRule.data.children,
        parentToRuleSegment.index,
      );

      // Remove parent group if we removed its last child;
      // this ensures that all groups will always have children
      if (parentRule.data.children.length === 0) {
        rebuildRulePaths(parentRule);
        return deleteRuleFromRuleTree({
          treeRoot: ruleTree,
          rule: parentRule,
        });
      }
    }

    if (isGroupRule(rule)) {
      // bubble up deleted rule children to parent level
      parentRule.data.children = deleteRuleFromChildren(
        parentRule.data.children,
        parentToRuleSegment.index,
        structuredClone(rule.data.children),
      );
    }

    rebuildRulePaths(parentRule);
  }

  return ruleTree;
}

export type RuleInsertProps = {
  relativelyPositionedRule: RuleBuilderRule;
  ruleToInsert: RuleBuilderRule;
  position: RuleInsertionPosition;
};

/**
* Inserts the specified rule into the rule tree, using relative rule positioning.
* Returns a copy of the tree.
*/
export function insertRuleIntoRuleTree(
  treeRoot: RuleTree,
  args: RuleInsertProps,
): RuleTree {
  const {
    relativelyPositionedRule,
    ruleToInsert,
    position,
  } = args;

  const ruleTree = structuredClone(treeRoot);
  const relativeRulePath = relativelyPositionedRule.data.path;

  if (shouldInsertIntoRelativeRule(position)) {
    const parentRule = findRule(ruleTree, { rulePath: relativeRulePath });

    // narrow type
    if (isGroupRule(parentRule)) {
      parentRule.data.children = position === ruleInsertionPosition.firstChild
        ? [ruleToInsert, ...parentRule.data.children]
        : [...parentRule.data.children, ruleToInsert];

      rebuildRulePaths(parentRule);

      return ruleTree;
    }
  }

  /*
  * To insert a threshold into a rule, we create a pointer to that rule's parent.
  * This allows us to maintain the ordering / integrity of the parent's children.
  */
  const parentRule = getParentRule(ruleTree, relativelyPositionedRule);

  // last leg of rule path that paths from parent -> rule to be inserted
  const parentToRuleSegment = getParentToChildRuleSegment(relativeRulePath);

  if (isGroupRule(parentRule)) {
    const parentChildren = parentRule.data.children;
    // re order parent children where the inserted rule is positioned
    // near the relatively positioned rule
    parentRule.data.children = [
      // preceding children to inserted rule
      ...parentChildren.slice(0, parentToRuleSegment.index),
      // insert rule
      ...(position === ruleInsertionPosition.before ? [ruleToInsert] : []),
      structuredClone(relativelyPositionedRule),
      ...(position === ruleInsertionPosition.after ? [ruleToInsert] : []),
      // following children to inserted rule
      ...parentChildren.slice(parentToRuleSegment.index + 1),
    ];

    rebuildRulePaths(parentRule);
  }

  return ruleTree;
}

export type GroupInsertProps = {
  parentRule: RuleBuilderRule;
  groupToInsert: GroupRule;
  /** start & end indices are inclusive */
  indices: {
    start: number;
    end: number;
  };
};

/**
* Assumes that start and end are in same group and shares a parent;
* Beginning and ending indices are both inclusive.
*/
export function groupRulesInRuleTree(
  treeRoot: RuleTree, args: GroupInsertProps,
): RuleTree {
  const {
    parentRule, groupToInsert, indices,
  } = args;
  const { start, end } = indices;

  const ruleTree = structuredClone(treeRoot);
  const parentGroup = findRule(ruleTree, { rulePath: parentRule.data.path });

  if (isGroupRule(parentGroup)) {
    groupToInsert.data.children = parentGroup.data.children
      .slice(start, end + 1);

    parentGroup.data.children = [
      ...parentGroup.data.children.slice(0, start),
      groupToInsert,
      ...parentGroup.data.children.slice(end + 1),
    ];

    rebuildRulePaths(parentGroup);
  }

  return ruleTree;
}

const toggleMap = {
  [groupTypeConsts.and]: groupTypeConsts.or,
  [groupTypeConsts.or]: groupTypeConsts.and,
};

export function toggleGroupTypeInRuleTree({
  treeRoot, rule,
}: {
  treeRoot: RuleTree;
  // the group to toggle
  rule: GroupRule;
}): RuleTree {
  const ruleTree = structuredClone(treeRoot);
  const groupToToggle = findRule(ruleTree, { rulePath: rule.data.path });

  if (isGroupRule(groupToToggle)) {
    groupToToggle.data.groupType = toggleMap[groupToToggle.data.groupType];
    groupToToggle.id = generatePublicId('group');
    rebuildRulePaths(groupToToggle);
  }

  return ruleTree;
}

/**
 * Computes mapping of rule id -> flat & linear rule line number.
 * Thresholds have only 1 index, represented by [index]
 * Non-ambiguous Groups have 2 indices, represented by [beginningIndex, endingIndex]
 * Ambiguous Groups have no indices, since only their children take up dedicated rule lines.
 * For posterity, we map their ids to an empty array.
 * Root groups (that don't render parentheses) also get empty array.
*/
export function mapRulesToIndices(
  treeRoot: RuleTree,
): Record<RuleBuilderRule['id'], number[]> {
  const ruleToIndex = {};
  // indexing begins at 1, not 0
  let indexCounter = 1;

  // Check if root is a group that won't render parentheses
  const isRootGroup = isGroupRule(treeRoot);

  function traverse(rule: RuleBuilderRule, isRoot: boolean = false) {
    if (isThresholdRule(rule)) {
      ruleToIndex[rule.id] = [indexCounter];
      indexCounter += 1;
      return;
    }

    // ambiguous group or root group (both don't render parentheses)
    if (rule.data.isAmbiguous || (isRoot && isRootGroup)) {
      ruleToIndex[rule.id] = [];
      rule.data.children.forEach(child => traverse(child, false));
      return;
    }

    // non-ambiguous group
    ruleToIndex[rule.id] = [indexCounter];
    indexCounter += 1;

    rule.data.children.forEach(child => traverse(child, false));

    ruleToIndex[rule.id].push(indexCounter);
    indexCounter += 1;
  }

  traverse(treeRoot, true);

  return ruleToIndex;
}

/**  Gets linear rule line number from ruleIdToIndexMap */
export function getRuleIndex(args: {
  ruleIdToIndexMap: ReturnType<typeof mapRulesToIndices>;
  rule: RuleBuilderRule;
  groupPosition?: 'start' | 'end';
}) {
  const { ruleIdToIndexMap, rule, groupPosition } = args;
  if (isThresholdRule(rule)) {
    return ruleIdToIndexMap[rule.id][0];
  }

  // groups
  if (rule.data.isAmbiguous) {
    // Does not have an index - fail safely
    return -1;
  }

  return ruleIdToIndexMap[rule.id][groupPosition === 'end' ? 1 : 0];
}

/**
 * Returns a mapping of the rendered depth of each rule.
 * Ambiguous groups do not add a layer of nesting and map to null.
 */
export function mapRuleToDepth(
  treeRoot: RuleTree,
): Record<RuleBuilderRule['id'], number | null> {
  const ruleToDepth = {};

  // Check if root is a group that won't render parentheses
  const isRootGroup = isGroupRule(treeRoot);

  function traverse(rule: RuleBuilderRule, depth: number) {
    ruleToDepth[rule.id] = isAmbiguousGroup(rule)
      ? null
      : depth;

    if (isGroupRule(rule)) {
      // ambiguous rules don't add another layer of nesting
      const nextDepth = rule.data.isAmbiguous
        ? depth
        : depth + 1;
      rule.data.children.forEach(child => traverse(child, nextDepth));
    }
  }

  // Start at depth -1 for root groups so their children start at depth 0
  const initialDepth = isRootGroup ? -1 : 0;
  traverse(treeRoot, initialDepth);

  return ruleToDepth;
}

/**
 * Removes the gate rule from rules. The gate is always a top-level child
 * of an AND group; this filters that one rule out and returns the rest.
 */
export function removeGateRuleFromRule(args: {
  rules: Rules;
  gateRuleId: Threshold['publicId'];
}): Rules {
  const { rules, gateRuleId } = args;

  if (!isBooleanRule(rules) || !Object.hasOwn(rules, groupTypeConsts.and)) {
    return rules;
  }
  const children = rules[groupTypeConsts.and];
  const filtered = children.filter(child => (
    !isIndexedRule(child) || child.id !== gateRuleId
  ));

  return filtered.length > 0
    ? { ...rules, [groupTypeConsts.and]: filtered }
    : {};
}
